import React, { useState } from 'react';
import toast, { Toast, resolveValue, useToaster } from 'react-hot-toast';
import { Check, X, Info, AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info' | 'loading';

interface LoadingToastProps {
  toast: Toast;
}

export function LoadingToast({ toast: t }: LoadingToastProps) {
  const [isHovered, setIsHovered] = useState(false);

  // 1. Detect variant from toast type or custom options / message content
  let variant: ToastVariant = 'info';
  if (t.type === 'success') {
    variant = 'success';
  } else if (t.type === 'error') {
    variant = 'error';
  } else if (t.type === 'loading') {
    variant = 'loading';
  } else if ((t as any).custom?.type === 'warning' || (typeof t.message === 'string' && /warning/i.test(t.message))) {
    variant = 'warning';
  }
  // 'info' is already the default, so no explicit branch is needed for it.

  // 2. Parse Message (omit generic titles like "Success!", "Error!", "Information!", etc.)
  let displayMessage: React.ReactNode = '';
  let subMessage: React.ReactNode = '';

  const rawMessage = resolveValue(t.message, t);

  if (typeof rawMessage === 'string') {
    if (rawMessage.includes('\n')) {
      const [first, ...rest] = rawMessage.split('\n');
      const isGenericHeader = /^(success!?|error!?|warning!?|info!?|information!?|loading\.{0,3})$/i.test(first.trim());
      if (isGenericHeader) {
        displayMessage = rest.join('\n').trim();
      } else {
        displayMessage = first.trim();
        subMessage = rest.join('\n').trim();
      }
    } else {
      displayMessage = rawMessage;
    }
  } else if (React.isValidElement(rawMessage)) {
    displayMessage = rawMessage;
  } else if (rawMessage && typeof rawMessage === 'object') {
    const rawTitle = (rawMessage as any).title;
    const rawDesc = (rawMessage as any).message || (rawMessage as any).description;
    const isGenericHeader = rawTitle && /^(success!?|error!?|warning!?|info!?|information!?|loading\.{0,3})$/i.test(String(rawTitle).trim());
    if (rawTitle && !isGenericHeader) {
      displayMessage = rawTitle;
      subMessage = rawDesc;
    } else {
      displayMessage = rawDesc || rawTitle || '';
    }
  }

  // 3. Compact variant styling with semantic color applied directly to the message
  const variantConfig = {
    info: {
      borderColor: 'border-sky-300 dark:border-sky-800/80',
      iconBg: 'bg-[#0284C7]',
      textColor: 'text-sky-700 dark:text-sky-300',
      ringColor: 'text-sky-500 dark:text-sky-400',
      icon: <Info className="size-3.5 text-white stroke-[2.5]" />,
    },
    success: {
      borderColor: 'border-green-300 dark:border-green-800/80',
      iconBg: 'bg-[#16A34A]',
      textColor: 'text-emerald-700 dark:text-emerald-300',
      ringColor: 'text-emerald-500 dark:text-emerald-400',
      icon: <Check className="size-3.5 text-white stroke-[3]" />,
    },
    error: {
      borderColor: 'border-red-300 dark:border-red-800/80',
      iconBg: 'bg-[#DC2626]',
      textColor: 'text-red-600 dark:text-rose-300',
      ringColor: 'text-rose-500 dark:text-rose-400',
      icon: <X className="size-3.5 text-white stroke-[3]" />,
    },
    warning: {
      borderColor: 'border-amber-300 dark:border-amber-800/80',
      iconBg: 'bg-[#D97706]',
      textColor: 'text-amber-700 dark:text-amber-300',
      ringColor: 'text-amber-500 dark:text-amber-400',
      icon: <AlertTriangle className="size-3 text-white stroke-[2.5]" />,
    },
    loading: {
      borderColor: 'border-blue-300 dark:border-blue-800/80',
      iconBg: 'bg-[#2563EB]',
      textColor: 'text-blue-700 dark:text-blue-300',
      ringColor: 'text-blue-500 dark:text-blue-400',
      icon: <Loader2 className="size-3.5 text-white animate-spin" />,
    },
  }[variant];

  const duration = t.duration || 4000;
  const isInfinite = variant === 'loading' || duration === Infinity;

  // Compact circular countdown ring metrics (size 24px: radius = 9.5)
  const radius = 9.5;
  const circumference = 2 * Math.PI * radius;

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        'group relative flex items-center gap-3 w-[360px] max-w-[calc(100vw-32px)] py-2.5 px-3.5 rounded-lg bg-card border shadow-lg shadow-slate-900/5 select-none overflow-hidden transition-all',
        variantConfig.borderColor
      )}
      role="alert"
    >
      {/* 1. Reduced compact icon disc */}
      <div className={cn('size-6 rounded-full shrink-0 flex items-center justify-center shadow-2xs', variantConfig.iconBg)}>
        {variantConfig.icon}
      </div>

      {/* 2. Middle Content: Direct message with green/red/etc color */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <p className={cn('text-[13px] font-semibold leading-snug break-words', variantConfig.textColor)}>
          {displayMessage}
        </p>
        {subMessage && (
          <p className="text-xs text-muted-foreground font-normal leading-relaxed break-words mt-0.5 line-clamp-2">
            {subMessage}
          </p>
        )}
      </div>

      {/* 3. Right compact circular countdown timer ring & close button */}
      <div className="relative size-6 shrink-0 flex items-center justify-center">
        <svg className="size-6 -rotate-90 pointer-events-none" viewBox="0 0 24 24">
          {/* Background circle track */}
          <circle
            cx="12"
            cy="12"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-slate-200 dark:text-slate-800"
          />

          {/* Animated loading / countdown progress stroke */}
          {isInfinite ? (
            <circle
              cx="12"
              cy="12"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * 0.4}
              className={cn(variantConfig.ringColor, 'animate-spin origin-center')}
            />
          ) : (
            <circle
              cx="12"
              cy="12"
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset="0"
              className={variantConfig.ringColor}
              style={{
                animation: `toast-ring-countdown ${duration}ms linear forwards`,
                animationPlayState: isHovered ? 'paused' : 'running',
              }}
            />
          )}
        </svg>

        {/* Center Close / Dismiss Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            toast.dismiss(t.id);
          }}
          aria-label="Dismiss notification"
          className="absolute inset-0 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
        >
          {variant === 'loading' ? (
            <Loader2 className="size-2.5 text-primary animate-spin" />
          ) : (
            <X className="size-3 stroke-[2.25]" />
          )}
        </button>
      </div>
    </div>
  );
}

// Global helper utility for clean typed toasts with titles and countdown
export const showToast = {
  success: (message: string, options?: any) => toast.success(message, options),
  error: (message: string, options?: any) => toast.error(message, options),
  warning: (message: string, options?: any) =>
    toast(message, {
      ...options,
      custom: { type: 'warning' },
      icon: '⚠️',
    }),
  info: (message: string, options?: any) =>
    toast(message, {
      ...options,
      custom: { type: 'info' },
      icon: 'ℹ️',
    }),
  loading: (message: string, options?: any) => toast.loading(message, options),
  dismiss: (id?: string) => toast.dismiss(id),
};

// Augment toast with warning and info helpers for convenience across hubs
if (typeof window !== 'undefined') {
  if (!(toast as any).warning) {
    (toast as any).warning = showToast.warning;
  }
  if (!(toast as any).info) {
    (toast as any).info = showToast.info;
  }
}

// Custom Toaster Container for clean one-down-one vertical stacking (no overlay)
export function AppToaster() {
  const { toasts, handlers } = useToaster({
    duration: 4000,
  });
  const { startPause, endPause } = handlers;

  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      onMouseEnter={startPause}
      onMouseLeave={endPause}
      className="fixed z-[99999] flex flex-col gap-2 pointer-events-none"
      style={{
        top: '72px',
        right: '24px',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto transition-all duration-300 ease-out"
          style={{
            opacity: t.visible ? 1 : 0,
            transform: t.visible ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.95)',
          }}
        >
          <LoadingToast toast={t} />
        </div>
      ))}
    </div>
  );
}
